import React, { useRef, useEffect, useState } from 'react';
import { HistoricalEvent } from '../../services/timeTunnelService';
import './TimeTunnel.css';

interface YearTimelineProps {
  years: number[];
  events: HistoricalEvent[];
  selectedYear: number | null;
  currentRange: { start: number; end: number };
  onYearClick: (year: number) => void;
  onRangeChange: (start: number, end: number) => void;
}

const YearTimeline: React.FC<YearTimelineProps> = ({
  years,
  events,
  selectedYear,
  currentRange,
  onYearClick,
  onRangeChange
}) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [hoveredYear, setHoveredYear] = useState<number | null>(null);
  const [zoomLevel, setZoomLevel] = useState<'compact' | 'normal' | 'expanded'>('normal');

  // Generate years in the current range
  const generateYearList = () => {
    const yearList: number[] = [];
    for (let year = currentRange.start; year <= currentRange.end; year++) {
      yearList.push(year);
    }
    return yearList;
  };

  const yearList = generateYearList();

  // Count events per year
  const getEventCount = (year: number) => {
    return events.filter(e => e.year === year).length;
  };

  // Get events by country for a year
  const getEventsByCountry = (year: number) => {
    const yearEvents = events.filter(e => e.year === year);
    const byCountry: { [country: string]: HistoricalEvent[] } = {};
    yearEvents.forEach(event => {
      if (!byCountry[event.country]) {
        byCountry[event.country] = [];
      }
      byCountry[event.country].push(event);
    });
    return byCountry;
  };

  // Get density level for visual styling
  const getDensityLevel = (eventCount: number): 'low' | 'medium' | 'high' | 'very-high' => {
    if (eventCount === 0) return 'low';
    if (eventCount <= 2) return 'low';
    if (eventCount <= 5) return 'medium';
    if (eventCount <= 10) return 'high';
    return 'very-high';
  };

  // Get max event count for normalization
  const getMaxEventCount = () => {
    return Math.max(...yearList.map(year => getEventCount(year)), 1);
  };

  // Scroll to selected year
  useEffect(() => {
    if (selectedYear && scrollContainerRef.current) {
      const yearElement = scrollContainerRef.current.querySelector(`[data-year="${selectedYear}"]`);
      if (yearElement) {
        yearElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }
    }
  }, [selectedYear]);

  const handleScrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const handleScrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  const handleJumpToYear = () => {
    const yearInput = prompt('Enter year to jump to:');
    if (yearInput) {
      const year = parseInt(yearInput);
      if (!isNaN(year)) {
        const newStart = Math.max(currentRange.start, year - 50);
        const newEnd = Math.min(currentRange.end, year + 50);
        onRangeChange(newStart, newEnd);
        setTimeout(() => {
          onYearClick(year);
        }, 100);
      }
    }
  };

  const handleExpandRange = (direction: 'left' | 'right', amount: number = 50) => {
    if (direction === 'left') {
      onRangeChange(currentRange.start - amount, currentRange.end);
    } else {
      onRangeChange(currentRange.start, currentRange.end + amount);
    }
  };

  const handleGoToToday = () => {
    const currentYear = new Date().getFullYear();
    const newStart = currentYear - 50;
    const newEnd = currentYear + 50;
    onRangeChange(newStart, newEnd);
    setTimeout(() => {
      onYearClick(currentYear);
    }, 100);
  };

  const handleDecadeJump = (direction: 'prev' | 'next') => {
    if (!selectedYear) return;
    const newYear = direction === 'prev' ? selectedYear - 10 : selectedYear + 10;
    if (newYear >= currentRange.start && newYear <= currentRange.end) {
      onYearClick(newYear);
    } else {
      const newStart = newYear - 50;
      const newEnd = newYear + 50;
      onRangeChange(newStart, newEnd);
      setTimeout(() => {
        onYearClick(newYear);
      }, 100);
    }
  };

  const handleZoomChange = (level: 'compact' | 'normal' | 'expanded') => {
    setZoomLevel(level);
  };

  const isDecadeMarker = (year: number) => {
    return year % 10 === 0;
  };

  const maxEventCount = getMaxEventCount();

  return (
    <div className="year-timeline-container">
      <div className="year-timeline-controls">
        <button onClick={handleScrollLeft} className="year-timeline-scroll-btn">◀</button>
        <button onClick={handleJumpToYear} className="year-timeline-jump-btn">Jump to Year</button>
        <button onClick={handleGoToToday} className="year-timeline-jump-btn">Go to Today</button>
        <button onClick={() => handleExpandRange('left')} className="year-timeline-expand-btn">◀◀</button>
        <span className="year-timeline-range">
          {currentRange.start} - {currentRange.end}
        </span>
        <button onClick={() => handleExpandRange('right')} className="year-timeline-expand-btn">▶▶</button>
        <button onClick={handleScrollRight} className="year-timeline-scroll-btn">▶</button>
      </div>

      <div className="year-timeline-controls year-timeline-controls-secondary">
        {selectedYear && (
          <>
            <button onClick={() => handleDecadeJump('prev')} className="year-timeline-decade-btn">◀ Prev Decade</button>
            <button onClick={() => handleDecadeJump('next')} className="year-timeline-decade-btn">Next Decade ▶</button>
          </>
        )}
        <div className="year-timeline-zoom-controls">
          <label>Zoom:</label>
          <button 
            onClick={() => handleZoomChange('compact')} 
            className={`year-timeline-zoom-btn ${zoomLevel === 'compact' ? 'active' : ''}`}
          >
            Compact
          </button>
          <button 
            onClick={() => handleZoomChange('normal')} 
            className={`year-timeline-zoom-btn ${zoomLevel === 'normal' ? 'active' : ''}`}
          >
            Normal
          </button>
          <button 
            onClick={() => handleZoomChange('expanded')} 
            className={`year-timeline-zoom-btn ${zoomLevel === 'expanded' ? 'active' : ''}`}
          >
            Expanded
          </button>
        </div>
      </div>

      <div className="year-timeline-density-bar">
        {yearList.map(year => {
          const eventCount = getEventCount(year);
          const heightPercent = maxEventCount > 0 ? (eventCount / maxEventCount) * 100 : 0;
          return (
            <div
              key={`density-${year}`}
              className="year-timeline-density-item"
              style={{ height: `${Math.max(heightPercent, 2)}%` }}
              title={`${year}: ${eventCount} events`}
            />
          );
        })}
      </div>

      <div className="year-timeline-scroll-container" ref={scrollContainerRef}>
        <div className={`year-timeline year-timeline-${zoomLevel}`} ref={timelineRef}>
          {yearList.map(year => {
            const eventCount = getEventCount(year);
            const hasEvents = eventCount > 0;
            const isSelected = selectedYear === year;
            const isYearWithData = years.includes(year);
            const densityLevel = getDensityLevel(eventCount);
            const isDecade = isDecadeMarker(year);
            const isHovered = hoveredYear === year;

            return (
              <React.Fragment key={year}>
                {isDecade && (
                  <div className="year-timeline-decade-marker">
                    <div className="year-timeline-decade-line"></div>
                    <div className="year-timeline-decade-label">{year}s</div>
                  </div>
                )}
                <div
                  data-year={year}
                  className={`year-timeline-node 
                    ${isSelected ? 'selected' : ''} 
                    ${hasEvents ? 'has-events' : ''} 
                    ${isYearWithData ? 'has-data' : ''} 
                    density-${densityLevel}
                    ${isHovered ? 'hovered' : ''}`}
                  onClick={() => onYearClick(year)}
                  onMouseEnter={() => setHoveredYear(year)}
                  onMouseLeave={() => setHoveredYear(null)}
                  title={`${year}${eventCount > 0 ? ` (${eventCount} events)` : ''}`}
                >
                  <div className="year-timeline-node-year">{year}</div>
                  {hasEvents && (
                    <div className="year-timeline-node-marker">
                      <div className="year-timeline-node-dot"></div>
                      {eventCount > 1 && (
                        <div className="year-timeline-node-count">{eventCount}</div>
                      )}
                    </div>
                  )}
                  
                  {isHovered && hasEvents && (
                    <div className="year-timeline-hover-card">
                      <div className="hover-card-header">
                        <strong>{year}</strong>
                        <span className="hover-card-total">{eventCount} event{eventCount !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="hover-card-content">
                        {Object.entries(getEventsByCountry(year)).map(([country, countryEvents]) => (
                          <div key={country} className="hover-card-country">
                            <span className="hover-card-country-name">{country}</span>
                            <span className="hover-card-country-count">{countryEvents.length}</span>
                          </div>
                        ))}
                        <div className="hover-card-events">
                          {events
                            .filter(e => e.year === year)
                            .slice(0, 3)
                            .map((event, idx) => (
                              <div key={idx} className="hover-card-event-preview">
                                • {event.title}
                              </div>
                            ))}
                          {eventCount > 3 && (
                            <div className="hover-card-more">
                              +{eventCount - 3} more...
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default YearTimeline;

